// 2026-06-26: Phase 14 — Audit Trail (NADCAP Common Core item 3, immutable records).
// Read-only view of mes_audit_log with hash-chain integrity check.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { cleanCustomerId } from "../../Services/app/operatorservice";
import Swal from 'sweetalert2';
import {
  TextField, MenuItem, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const ACTION_COLOR = {
  OP_START:           { color: BR.blue,   bg: '#dbeafe', label: 'OP START' },
  OP_CLAIM_PARTIAL:   { color: BR.amber,  bg: '#fef3c7', label: 'CLAIM (PARTIAL)' },
  OP_CLAIM_COMPLETE:  { color: BR.green,  bg: '#dcfce7', label: 'CLAIM (COMPLETE)' },
  OP_PAUSE:           { color: BR.orange, bg: '#fff7ed', label: 'PAUSE' },
  OP_RESUME:          { color: BR.blue,   bg: '#dbeafe', label: 'RESUME' },
  QA_SIGNOFF:         { color: BR.green,  bg: '#dcfce7', label: 'QA SIGN-OFF' },
  QUAL_CHECK_WARN:    { color: BR.red,    bg: '#fee2e2', label: '⚠ QUAL WARN' },
  WO_RELEASE:         { color: BR.blue,   bg: '#dbeafe', label: 'WO RELEASE' },
  WO_COMPLETE:        { color: BR.green,  bg: '#dcfce7', label: 'WO COMPLETE' },
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

export default function AuditTrail() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, last_24h: 0, last_7d: 0, actors: 0, by_action_7d: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('ALL');
  const [filterEntity, setFilterEntity] = useState('ALL');
  const [detail, setDetail] = useState(null);
  const [integrity, setIntegrity] = useState(null);

  const BASE = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${BASE}/api/audit-log`, { params: { customer_id: customerId, limit: 500 } }),
        axios.get(`${BASE}/api/audit-log/stats`, { params: { customer_id: customerId } }),
      ]);
      setRows(a.data || []);
      setStats(b.data || {});
    } catch (e) {
      console.warn('audit log fetch failed:', e?.message);
    } finally { setLoading(false); }
  }, [customerId, BASE]);

  useEffect(() => { refresh(); }, [refresh]);

  async function verifyChain() {
    try {
      const r = await axios.get(`${BASE}/api/audit-log/verify`, { params: { customer_id: customerId } });
      setIntegrity(r.data);
      Swal.fire({
        icon: r.data.intact ? 'success' : 'error',
        title: r.data.intact ? '✓ Audit chain intact' : '⚠ Chain tampering detected',
        html: `Walked <strong>${r.data.checked}</strong> events.<br/>` +
              (r.data.intact
                ? `Last signature: <code style="font-size:10px">${(r.data.last_signature || '').slice(0,32)}…</code>`
                : `First mismatch at row <strong>#${r.data.first_bad_id}</strong>. Records before this point are trustworthy; after this point should be investigated.`),
        confirmButtonColor: r.data.intact ? BR.green : BR.red,
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Verify failed', text: e.message, confirmButtonColor: BR.orange });
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filterAction !== 'ALL' && r.action !== filterAction) return false;
      if (filterEntity !== 'ALL' && r.entity_type !== filterEntity) return false;
      if (!q) return true;
      return (r.entity_ref || '').toLowerCase().includes(q)
          || (r.actor_name || '').toLowerCase().includes(q)
          || (r.action || '').toLowerCase().includes(q)
          || (r.entity_id || '').toLowerCase().includes(q);
    });
  }, [rows, search, filterAction, filterEntity]);

  const distinctActions = useMemo(() =>
    Array.from(new Set(rows.map(r => r.action))).sort(), [rows]);
  const distinctEntities = useMemo(() =>
    Array.from(new Set(rows.map(r => r.entity_type))).sort(), [rows]);

  function exportCsv() {
    const cols = ['ts','entity_type','entity_id','entity_ref','action','actor_id','actor_name','source','reason','signature'];
    const csv = [cols.join(',')]
      .concat(filtered.map(r => cols.map(c => {
        const v = r[c] == null ? '' : String(r[c]).replace(/"/g, '""');
        return `"${v}"`;
      }).join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            🔒 Audit Trail
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Append-only event log · ALCOA-aligned · SHA-256 hash-chain integrity · NADCAP Common Core #3
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={verifyChain}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '9px 14px', background: '#fff', color: BR.green,
                            border: `2px solid ${BR.green}`, borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
            <VerifiedIcon fontSize="small" /> Verify chain
          </button>
          <button onClick={exportCsv} disabled={filtered.length === 0}
                  style={{ padding: '9px 14px', background: BR.blue, color: '#fff', border: 'none',
                            borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer',
                            opacity: filtered.length === 0 ? 0.5 : 1 }}>
            Export CSV
          </button>
          <button onClick={refresh}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '9px 14px', background: BR.orange, color: '#fff',
                            border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
            <RefreshIcon fontSize="small" /> Refresh
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Total events', value: stats.total || 0,    color: BR.dark },
          { label: 'Last 24h',     value: stats.last_24h || 0, color: BR.blue },
          { label: 'Last 7d',      value: stats.last_7d || 0,  color: BR.green },
          { label: 'Distinct actors', value: stats.actors || 0,color: BR.amber },
        ].map(t => (
          <div key={t.label} style={{ background: '#fff', border: `1.5px solid ${BR.border}`,
                                       borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {t.value}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search ref, actor, action, entity_id…"
               style={{ flex: 1, minWidth: 280, padding: '8px 12px', border: `1px solid ${BR.border}`,
                         borderRadius: 6, fontSize: 13 }} />
        <TextField label="Action" size="small" select value={filterAction}
                   onChange={e => setFilterAction(e.target.value)}
                   sx={{ minWidth: 160 }}>
          <MenuItem value="ALL">All actions</MenuItem>
          {distinctActions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <TextField label="Entity" size="small" select value={filterEntity}
                   onChange={e => setFilterEntity(e.target.value)}
                   sx={{ minWidth: 160 }}>
          <MenuItem value="ALL">All entities</MenuItem>
          {distinctEntities.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No events {rows.length === 0 ? 'recorded yet — start a WO operation to populate' : 'matching filters'}.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['When','Entity','Ref','Action','Actor','Source','Signature',''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const info = ACTION_COLOR[r.action] || { color: BR.muted, bg: '#f3f4f6', label: r.action };
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontSize: 11 }}>{fmtDate(r.ts)}</td>
                    <td style={{ padding: '9px 10px', color: BR.muted, fontSize: 11 }}>{r.entity_type}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{r.entity_ref || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontWeight: 700 }}>{r.actor_name || '—'}</td>
                    <td style={{ padding: '9px 10px', color: BR.muted, fontSize: 11 }}>{r.source || '—'}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontSize: 10, color: BR.muted }}>
                      {(r.signature || '').slice(0, 12)}…
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                      <Tooltip title="View payload"><IconButton size="small" onClick={() => setDetail(r)}>
                        <VisibilityIcon fontSize="small" sx={{ color: BR.blue }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>ALCOA compliance:</strong> Every row is
        <strong> Attributable</strong> (actor_id + name),
        <strong> Legible</strong>,
        <strong> Contemporaneous</strong> (server-side ts),
        <strong> Original</strong> (append-only, no edits) and
        <strong> Accurate</strong> (SHA-256 chained signature). The "Verify chain" button replays every signature; a mismatch flags exactly which record was tampered with.
      </div>

      {detail && (
        <Dialog open onClose={() => setDetail(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>
            Audit record #{detail.id} · {detail.action}
          </DialogTitle>
          <DialogContent dividers>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, fontSize: 12, marginBottom: 12 }}>
              <div><strong>Timestamp:</strong> {fmtDate(detail.ts)}</div>
              <div><strong>Entity:</strong> {detail.entity_type} {detail.entity_ref && `· ${detail.entity_ref}`}</div>
              <div><strong>Entity ID:</strong> <code style={{ fontSize: 10 }}>{detail.entity_id}</code></div>
              <div><strong>Action:</strong> {detail.action}</div>
              <div><strong>Actor:</strong> {detail.actor_name} ({detail.actor_id})</div>
              <div><strong>Source:</strong> {detail.source}</div>
              {detail.reason && <div style={{ gridColumn: '1 / span 2' }}><strong>Reason:</strong> {detail.reason}</div>}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: BR.muted, marginBottom: 4 }}>PAYLOAD</div>
            <pre style={{ background: BR.bg, padding: 10, borderRadius: 4, fontSize: 11,
                           border: `1px solid ${BR.border}`, overflow: 'auto', maxHeight: 240 }}>
              {detail.payload ? JSON.stringify(detail.payload, null, 2) : '(none)'}
            </pre>
            <div style={{ fontSize: 11, fontWeight: 800, color: BR.muted, margin: '12px 0 4px' }}>SIGNATURE</div>
            <code style={{ fontSize: 10, color: BR.dark, display: 'block', background: BR.bg, padding: 8, borderRadius: 4, wordBreak: 'break-all' }}>
              {detail.signature}
            </code>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetail(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}
